import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const authHeader = (): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/** Escopo de aplicação da janela (at-53). */
export type PausaAutomaticaEscopo = 'TODOS' | 'ESPECIFICOS';

export interface ColaboradorDaJanela {
  id: string;
  nome: string;
}

export interface TipoDePausa {
  id: string;
  nome: string;
  totalJanelas?: number;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Janelas de pausa automática (at-48, ampliadas na at-53). Os horários trafegam em MINUTOS desde a
 * meia-noite no fuso da oficina; a resposta traz também `horaInicio`/`horaFim` já formatados.
 *
 * at-53: `escopo` decide o público — `TODOS` (comportamento original) ou `ESPECIFICOS`, restrito a
 * `colaboradores`. É o que permite "Almoço 1" às 11:30 e "Almoço 2" às 12:30 convivendo.
 */
export interface PausaAutomatica {
  id: string;
  nome: string;
  tipoId: string | null;
  tipo: TipoDePausa | null;
  /** 0 = domingo … 6 = sábado. */
  diasSemana: number[];
  horaInicioMinutos: number;
  horaFimMinutos: number;
  horaInicio: string;
  horaFim: string;
  escopo: PausaAutomaticaEscopo;
  colaboradores: ColaboradorDaJanela[];
  ativa: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PausaAutomaticaPayload {
  nome: string;
  tipoId?: string | null;
  diasSemana: number[];
  horaInicioMinutos: number;
  horaFimMinutos: number;
  escopo?: PausaAutomaticaEscopo;
  colaboradorIds?: string[];
  ativa?: boolean;
}

/**
 * Horário de funcionamento por dia da semana (at-53).
 *
 * Lista possivelmente PARCIAL: dia ausente **não restringe nada** (sem desconto e sem bloqueio de
 * tela). É assim que a feature nasce, com a configuração vazia.
 */
export interface DiaExpediente {
  id?: string;
  /** 0 = domingo … 6 = sábado. */
  diaSemana: number;
  aberto: boolean;
  aberturaMinutos: number | null;
  fechamentoMinutos: number | null;
  abertura?: string | null;
  fechamento?: string | null;
}

export interface CoberturaDeTipo {
  tipo: TipoDePausa;
  totalColaboradores: number;
  totalCobertos: number;
  naoCobertos: ColaboradorDaJanela[];
}

/**
 * Resposta da rota aberta ao COLABORADOR (at-48b). O `agora` é o horário do SERVIDOR: o front
 * usa a diferença entre ele e o relógio local para agendar o bloqueio pelo relógio do servidor,
 * de modo que máquina com hora errada não trave fora de hora.
 *
 * at-53: as janelas já vêm filtradas para o usuário do token, e o expediente acompanha — a tela
 * bloqueia tanto na janela de pausa quanto fora do horário de funcionamento.
 */
export interface PausasVigentesResponse {
  agora: string;
  janelas: PausaAutomatica[];
  expediente: DiaExpediente[];
}

export async function getPausasVigentes(): Promise<PausasVigentesResponse> {
  const response = await axios.get(`${BACKEND_URL}/pausas-automaticas/vigentes`, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data;
}

export async function getPausasAutomaticas(): Promise<PausaAutomatica[]> {
  const response = await axios.get(`${BACKEND_URL}/pausas-automaticas`, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data;
}

export async function createPausaAutomatica(
  data: PausaAutomaticaPayload,
): Promise<PausaAutomatica> {
  const response = await axios.post(`${BACKEND_URL}/pausas-automaticas`, data, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data;
}

export async function updatePausaAutomatica(
  id: string,
  data: Partial<PausaAutomaticaPayload>,
): Promise<PausaAutomatica> {
  const response = await axios.patch(`${BACKEND_URL}/pausas-automaticas/${id}`, data, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data;
}

export async function deletePausaAutomatica(id: string): Promise<void> {
  await axios.delete(`${BACKEND_URL}/pausas-automaticas/${id}`, {
    headers: authHeader(),
    withCredentials: true,
  });
}

// --- Tipos de pausa (at-53) ---

export async function getTiposDePausa(): Promise<TipoDePausa[]> {
  const response = await axios.get(`${BACKEND_URL}/pausas-automaticas/tipos`, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data;
}

export async function createTipoDePausa(nome: string): Promise<TipoDePausa> {
  const response = await axios.post(
    `${BACKEND_URL}/pausas-automaticas/tipos`,
    { nome },
    { headers: authHeader(), withCredentials: true },
  );
  return response.data;
}

export async function updateTipoDePausa(id: string, nome: string): Promise<TipoDePausa> {
  const response = await axios.patch(
    `${BACKEND_URL}/pausas-automaticas/tipos/${id}`,
    { nome },
    { headers: authHeader(), withCredentials: true },
  );
  return response.data;
}

export async function deleteTipoDePausa(id: string): Promise<{ janelasSemTipo: number }> {
  const response = await axios.delete(`${BACKEND_URL}/pausas-automaticas/tipos/${id}`, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data;
}

// --- Expediente (at-53) ---

export async function getExpediente(): Promise<DiaExpediente[]> {
  const response = await axios.get(`${BACKEND_URL}/pausas-automaticas/expediente`, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data;
}

/** PUT declarativo: a lista enviada passa a ser a verdade, e dia ausente deixa de restringir. */
export async function setExpediente(dias: DiaExpediente[]): Promise<DiaExpediente[]> {
  const response = await axios.put(
    `${BACKEND_URL}/pausas-automaticas/expediente`,
    {
      dias: dias.map((dia) => ({
        diaSemana: dia.diaSemana,
        aberto: dia.aberto,
        ...(dia.aberto
          ? { aberturaMinutos: dia.aberturaMinutos, fechamentoMinutos: dia.fechamentoMinutos }
          : {}),
      })),
    },
    { headers: authHeader(), withCredentials: true },
  );
  return response.data;
}

// --- Cobertura (at-53) ---

export async function getCobertura(): Promise<CoberturaDeTipo[]> {
  const response = await axios.get(`${BACKEND_URL}/pausas-automaticas/cobertura`, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data;
}

/** "12:00" → 720. Devolve null quando o input está vazio ou malformado. */
export function horaParaMinutos(hora: string): number | null {
  const casa = /^(\d{1,2}):(\d{2})$/.exec(hora?.trim() ?? '');
  if (!casa) return null;
  const horas = Number(casa[1]);
  const minutos = Number(casa[2]);
  if (horas > 23 || minutos > 59) return null;
  return horas * 60 + minutos;
}

/** 720 → "12:00" (para preencher o input time na edição). */
export function minutosParaHora(minutos: number): string {
  const normalizado = ((Math.trunc(minutos) % 1440) + 1440) % 1440;
  const horas = String(Math.floor(normalizado / 60)).padStart(2, '0');
  const restante = String(normalizado % 60).padStart(2, '0');
  return `${horas}:${restante}`;
}
